import React, { useState, useCallback } from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SidePanel } from '@securspace/securspace-ui-kit';
import SimpleConfirmationDialog from '../SimpleConfirmationDialog';
import { BookingCheckInVO } from '../../types/model/valueObject/booking/BookingCheckInVO';
import { useCheckInSubmit } from '../../hooks/checkin/useCheckInSubmit';
import CheckInForm from './CheckInForm/CheckInForm';
import {useSnackbar} from "../../context/snackbar-context";
import { ReportDialogProps } from "../../interfaces";
import type {CheckInFormSubmissionValues} from '../../hooks/checkin/useCheckInSubmit/interfaces';

/**
 * Side panel container for the Check-In flow.
 * Manages panel open/close state, unsaved changes protection,
 * and delegates to CheckInForm for form rendering.
 *
 * @param open Whether the panel is visible
 * @param booking The booking to check in against
 * @param onClose Callback when panel is closed
 * @param onCheckInSuccess Callback after successful check-in (triggers parent refresh)
 */
const CheckInPanel = ({
    open,
    item: booking,
    onClose
}: ReportDialogProps<BookingCheckInVO>) => {
  // Form dirty tracking
  const [formDirty, setFormDirty] = useState(false);

  // Dialog state
  const [discardChangesDialog, setDiscardChangesDialog] = useState(false);

  // Check-in submission hook
  const { submit, loading: submitLoading } = useCheckInSubmit();
  const { snackbarShowMessage } = useSnackbar();

  const resetState = useCallback(() => {
    setFormDirty(false);
    setDiscardChangesDialog(false);
  }, []);

  const handleClose = useCallback(
    (forceClose: boolean = false) => {
      if (forceClose) {
        resetState();
      }
      setDiscardChangesDialog(false);
      onClose({item: booking});
    },
    [onClose, resetState, booking]
  );

  /**
   * Called by SidePanel when user attempts to close (X, outside click).
   * Returns false to block close if there are unsaved changes.
   */
  const handleCloseAttempt = useCallback((): boolean => {
    if (formDirty) {
      setDiscardChangesDialog(true);
      return false;
    }
    resetState();
    return true;
  }, [formDirty, resetState]);

  /**
   * Called by the form's "Cancel" button (within the legacy form components).
   */
  const handleCancel = useCallback(() => {
    if (formDirty) {
      setDiscardChangesDialog(true);
    } else {
      handleClose(true);
    }
  }, [formDirty, handleClose]);

  /**
   * Handles check-in form submission.
   */
  const handleFormSave = useCallback(
    async (formValues: CheckInFormSubmissionValues) => {
      if (!booking) return;

      const locationId = booking.locationId;
      const buyerId = booking.buyerAccountId;

      if (!locationId || !buyerId) {
        snackbarShowMessage('Missing booking data. Please try again.', 'error');
        return;
      }

      const result = await submit({
        bookingId: booking.bookingId,
        locationId,
        buyerId,
        correlationId: formValues.correlationId ?? null,
        equipmentDataErrorMessage: formValues.equipmentDataErrorMessage ?? null,
        formValues,
      });

      if (!result.checkInSucceeded) {
        // Full failure — keep panel open so the user can retry
        if (result.errorMessage) {
          snackbarShowMessage(result.errorMessage, 'error');
        }
        return;
      }

      if (result.gallerySucceeded === false) {
        // Partial success — check-in saved, images failed
        if (result.errorMessage) {
          snackbarShowMessage(result.errorMessage, 'warning');
        }
      } else {
        snackbarShowMessage('Successfully checked in!', 'success');
      }

      resetState();
      onClose({item: booking, action: 'SUCCESS'});
    },
    [booking, submit, snackbarShowMessage, resetState, onClose]
  );

  const panelTitle = booking
    ? `Check In — ${booking.buyerCompanyName || 'Booking'}`
    : 'Check In Asset';

  return (
    <>
      <SidePanel
        open={open}
        onClose={() => onClose({item: booking})}
        onCloseAttempt={handleCloseAttempt}
        title={panelTitle}
      >
        <CheckInForm
          onFormSave={handleFormSave}
          onCancel={handleCancel}
          onDirtyChange={setFormDirty}
          loading={submitLoading}
        />
      </SidePanel>

      {/* Discard Unsaved Changes Dialog */}
      <SimpleConfirmationDialog
        open={discardChangesDialog}
        onClose={() => setDiscardChangesDialog(false)}
        onConfirm={() => handleClose(true)}
        title={
          <>
            <WarningAmberIcon color="warning" sx={{ mr: 1, verticalAlign: 'middle' }} />
            Discard Unsaved Changes?
          </>
        }
        body="You have unsaved changes in the form. Are you sure you want to close and lose your progress?"
      />
    </>
  );
};

export const CheckInPanelWithSnackbar = CheckInPanel;
